/* MedStudy homepage redesign — JS for templates/index.redesign.json sections.
 * Single IIFE, runs once per page. Each init() function is a no-op when its
 * markup isn't present, so this file can be loaded by any redesign section.
 */

(function () {
  if (window.__medStudyHomepageRedesignInit) return;
  window.__medStudyHomepageRedesignInit = true;

  function initScrollReveal() {
    var els = document.querySelectorAll('.hpr-reveal');
    if (!els.length) return;
    // Without IntersectionObserver we never arm the hidden state — everything
    // stays visible (CSS only hides .hpr-reveal under html.hpr-anim).
    if (!('IntersectionObserver' in window)) return;
    // Pre-mark blocks already in view so arming the hidden state below
    // doesn't blink content the user can currently see.
    var vh = window.innerHeight || document.documentElement.clientHeight;
    els.forEach(function (el) {
      if (el.getBoundingClientRect().top < vh * 0.9) el.classList.add('hpr-reveal-in');
    });
    document.documentElement.classList.add('hpr-anim');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('hpr-reveal-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    els.forEach(function (el) {
      if (!el.classList.contains('hpr-reveal-in')) io.observe(el);
    });
  }

  function initFindProductsDropdown() {
    var wraps = document.querySelectorAll('.hpr-dropdown-wrap');
    if (!wraps.length) return;

    // Disclosure pattern (a11y audit F4): the trigger is a button with
    // aria-expanded + aria-controls; the panel is a plain list of links. The
    // CSS closed state is visibility:hidden, so closed links are never in the
    // tab order. Escape and tabbing out of the panel close it; Escape also
    // returns focus to the trigger when focus was inside the panel.
    function closeMenu(menu, restoreFocus) {
      var w = menu.closest('.hpr-dropdown-wrap');
      var t = w ? w.querySelector('.hpr-dropdown-trigger') : null;
      var hadFocusInside = w && w.contains(document.activeElement) && document.activeElement !== t;
      menu.classList.remove('hpr-open');
      if (t) {
        t.setAttribute('aria-expanded', 'false');
        if (restoreFocus && hadFocusInside) t.focus();
      }
    }

    function closeAll(except, restoreFocus) {
      document.querySelectorAll('.hpr-dropdown-menu.hpr-open').forEach(function (m) {
        if (m === except) return;
        closeMenu(m, restoreFocus);
      });
    }

    wraps.forEach(function (wrap) {
      var trigger = wrap.querySelector('.hpr-dropdown-trigger');
      var menu = wrap.querySelector('.hpr-dropdown-menu');
      if (!trigger || !menu) return;
      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        closeAll(menu, false);
        var isOpen = menu.classList.contains('hpr-open');
        menu.classList.toggle('hpr-open', !isOpen);
        trigger.setAttribute('aria-expanded', String(!isOpen));
      });
      // ArrowDown on the trigger moves into the list (optional convenience;
      // Tab already reaches the first link because the panel follows the
      // trigger in the DOM).
      trigger.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowDown' || !menu.classList.contains('hpr-open')) return;
        var first = menu.querySelector('.hpr-dropdown-item');
        if (first) { e.preventDefault(); first.focus(); }
      });
      // Close when focus leaves the whole widget (Tab past the last link, or
      // Shift+Tab back past the trigger) so a hidden-but-open panel never lingers.
      wrap.addEventListener('focusout', function (e) {
        if (!menu.classList.contains('hpr-open')) return;
        if (e.relatedTarget && wrap.contains(e.relatedTarget)) return;
        closeMenu(menu, false);
      });
    });

    document.addEventListener('click', function () { closeAll(null, false); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAll(null, true);
    });
  }

  function initTestimonialCarousel() {
    var viewports = document.querySelectorAll('[data-hpr-carousel-viewport]');
    if (!viewports.length) return;

    var DELAY = 10000; // 10s — slow auto-advance per design call

    viewports.forEach(function (viewport) {
      var section = viewport.closest('[data-hpr-section="testimonials"]');
      if (!section) return;
      var track    = viewport.querySelector('[data-hpr-carousel-track]');
      var slides   = track ? track.querySelectorAll('.hpr-carousel-slide') : [];
      var dotsWrap = section.querySelector('[data-hpr-carousel-dots]');
      var prevBtn  = section.querySelector('[data-hpr-carousel-prev]');
      var nextBtn  = section.querySelector('[data-hpr-carousel-next]');
      if (!track || !slides.length) return;

      var current = 0;
      var timer   = null;
      var total   = slides.length;

      // Build dots
      if (dotsWrap) {
        dotsWrap.innerHTML = '';
        for (var i = 0; i < total; i++) {
          var d = document.createElement('button');
          d.type = 'button';
          d.className = 'hpr-carousel-dot' + (i === 0 ? ' hpr-active' : '');
          d.setAttribute('aria-label', 'Go to review ' + (i + 1));
          if (i === 0) d.setAttribute('aria-current', 'true');
          (function (idx) { d.addEventListener('click', function () { goTo(idx); }); })(i);
          dotsWrap.appendChild(d);
        }
      }

      function applyTransform() {
        var active = slides[current];
        if (!active) return;
        // Pixel-perfect centering: shift the track so the active slide's
        // center aligns with the viewport's center.
        var offset = (viewport.clientWidth - active.offsetWidth) / 2 - active.offsetLeft;
        track.style.transform = 'translateX(' + offset + 'px)';
      }

      function setActive(index) {
        for (var i = 0; i < total; i++) {
          var isActive = i === index;
          slides[i].classList.toggle('hpr-active', isActive);
          // Peek slides show at 30% opacity but are not the current slide:
          // hide them from assistive tech and take them out of the tab order
          // (a11y audit F5 — otherwise their 1.9:1 text is "content").
          if (isActive) {
            slides[i].removeAttribute('aria-hidden');
            slides[i].removeAttribute('inert');
          } else {
            slides[i].setAttribute('aria-hidden', 'true');
            slides[i].setAttribute('inert', '');
          }
          if (dotsWrap && dotsWrap.children[i]) {
            dotsWrap.children[i].classList.toggle('hpr-active', isActive);
            if (isActive) dotsWrap.children[i].setAttribute('aria-current', 'true');
            else dotsWrap.children[i].removeAttribute('aria-current');
          }
        }
      }

      function goTo(index) {
        current = (index + total) % total;
        setActive(current);
        applyTransform();
        resetTimer();
      }

      // Auto-rotation (WCAG 2.2.2, a11y audit F5). The visible Pause/Play
      // button owns the user's choice; pointer hover and keyboard focus pause
      // temporarily; reduced-motion users start paused (Play still works).
      var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      var userPaused = reduceMotion;
      var hovering = false;
      var focused  = false;
      var pauseBtn = section.querySelector('[data-hpr-carousel-pause]');

      function stopTimer() {
        clearInterval(timer);
        timer = null;
      }
      function startTimer() {
        stopTimer();
        if (userPaused || hovering || focused) return;
        timer = setInterval(function () { goTo(current + 1); }, DELAY);
      }
      function resetTimer() { startTimer(); }

      function renderPauseState() {
        if (pauseBtn) {
          pauseBtn.setAttribute('aria-pressed', String(userPaused));
          pauseBtn.setAttribute('aria-label', userPaused ? 'Play automatic rotation' : 'Pause automatic rotation');
        }
        // APG: announce slide changes only while the carousel is NOT rotating
        // on its own (aria-live off while auto-rotating, polite when paused).
        viewport.setAttribute('aria-live', userPaused ? 'polite' : 'off');
      }

      // Initial state
      setActive(0);
      // Defer the first transform until layout has settled (fonts, images).
      requestAnimationFrame(applyTransform);
      window.addEventListener('load', applyTransform);
      renderPauseState();
      startTimer();

      if (prevBtn) prevBtn.addEventListener('click', function () { goTo(current - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { goTo(current + 1); });
      if (pauseBtn) pauseBtn.addEventListener('click', function () {
        userPaused = !userPaused;
        renderPauseState();
        startTimer();
      });

      viewport.addEventListener('mouseenter', function () { hovering = true; stopTimer(); });
      viewport.addEventListener('mouseleave', function () { hovering = false; startTimer(); });

      // Keyboard focus anywhere in the section pauses rotation; it resumes
      // when focus leaves the section (unless the user pressed Pause).
      section.addEventListener('focusin', function () { focused = true; stopTimer(); });
      section.addEventListener('focusout', function (e) {
        if (e.relatedTarget && section.contains(e.relatedTarget)) return;
        focused = false;
        startTimer();
      });

      // Arrow keys move slides only while focus is on the carousel itself
      // (its controls or viewport) — never from the dropdown, links or CTAs
      // further down the section.
      var controls = section.querySelector('.hpr-carousel-controls');
      function inCarousel(target) {
        return (controls && controls.contains(target)) || viewport.contains(target);
      }
      section.addEventListener('keydown', function (e) {
        if (!inCarousel(e.target)) return;
        if (e.key === 'ArrowLeft')  { e.preventDefault(); goTo(current - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current + 1); }
      });

      // Recompute centering on resize
      var resizeRaf = null;
      window.addEventListener('resize', function () {
        if (resizeRaf) cancelAnimationFrame(resizeRaf);
        resizeRaf = requestAnimationFrame(applyTransform);
      });
    });
  }

  // ---- Spotlight motion loop (a11y F12 / WCAG 2.2.2) ------------------------
  // Muted UI loop inside the PT spotlight's window chrome. The <video> carries
  // no `autoplay` attribute: playback starts here, only while the frame is in
  // view, and never for reduced-motion or data-saver visitors (they keep the
  // poster; the Play button still works). The pause button owns the user's
  // choice — the viewport observer never overrides it.
  function initMotionLoops() {
    var wraps = document.querySelectorAll('[data-hpr-motion]');
    if (!wraps.length) return;
    var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    var saveData = !!(navigator.connection && navigator.connection.saveData);

    Array.prototype.forEach.call(wraps, function (wrap) {
      var video = wrap.querySelector('[data-hpr-motion-video]');
      var btn   = wrap.querySelector('[data-hpr-motion-pause]');
      if (!video) return;
      var userPaused = reduceMotion || saveData;
      var inView = false;

      function render() {
        if (!btn) return;
        btn.setAttribute('aria-pressed', String(userPaused));
        btn.setAttribute('aria-label', userPaused ? 'Play animation' : 'Pause animation');
      }
      function play() {
        video.muted = true; // belt-and-braces for autoplay policy
        var p = video.play();
        if (p && p.catch) {
          p.catch(function () {
            // Autoplay refused (rare with muted+playsinline): fall back to the
            // poster and let the button's click gesture start it.
            userPaused = true;
            render();
          });
        }
      }
      function pause() { if (!video.paused) video.pause(); }

      if (btn) {
        btn.addEventListener('click', function () {
          userPaused = !userPaused;
          render();
          if (userPaused) pause(); else play();
        });
      }

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            inView = entry.isIntersecting && entry.intersectionRatio >= 0.25;
            if (inView && !userPaused) play();
            if (!inView) pause();
          });
        }, { threshold: [0, 0.25] });
        io.observe(wrap);
      } else {
        inView = true;
        if (!userPaused) play();
      }

      render();
    });
  }

  // ---------------------------------------------------------------------------
  // Section analytics (added 2026-10-06).
  // Publishes Shopify custom customer events so the GA4 custom pixel
  // (admin > Settings > Customer events; see docs/ga4-section-events.md) can
  // forward them. The theme never talks to GA4 directly (layout/theme.liquid).
  //   medstudy:section_view   once per [data-hpr-section] per page view, when
  //                            >= 50% of the section, or >= 50% of the viewport,
  //                            is on screen
  //   medstudy:section_exit   once per section, the first time it leaves view
  //                            after a view (dwell_seconds); pagehide / tab
  //                            hidden flushes sections still on screen
  //   medstudy:section_click  any <a> / <button> click inside a section
  // Every publish is mirrored into window.__hprEvents for QA on stores with no
  // pixel (the dev store). Add ?hpr_debug=1 to the URL to console.log them.
  function initSectionAnalytics() {
    var sections = document.querySelectorAll('[data-hpr-section]');
    if (!sections.length) return;
    var pagePath = window.location.pathname;
    var log = (window.__hprEvents = window.__hprEvents || []);
    var debug = /[?&]hpr_debug=1/.test(window.location.search);

    function publish(name, data) {
      data.page_path = pagePath;
      log.push({ name: name, data: data, t: Date.now() });
      if (debug && window.console) console.log('[hpr-analytics]', name, data);
      try {
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish('medstudy:' + name, data);
        }
      } catch (e) { /* analytics must never break the page */ }
    }

    var state = [];
    Array.prototype.forEach.call(sections, function (el, i) {
      state.push({
        el: el,
        name: el.getAttribute('data-hpr-section') || ('section-' + (i + 1)),
        index: i + 1,
        viewed: false,
        exited: false,
        enteredAt: 0,
        dwell: 0
      });
    });
    function stateFor(el) {
      for (var i = 0; i < state.length; i++) if (state[i].el === el) return state[i];
      return null;
    }
    function base(s) { return { section: s.name, section_index: s.index }; }
    function exitSection(s, now) {
      if (s.enteredAt) { s.dwell += now - s.enteredAt; s.enteredAt = 0; }
      if (s.viewed && !s.exited) {
        s.exited = true;
        var d = base(s);
        d.dwell_seconds = Math.round(s.dwell / 100) / 10;
        publish('section_exit', d);
      }
    }

    if ('IntersectionObserver' in window) {
      var steps = [];
      for (var t = 0; t <= 20; t++) steps.push(t / 20);
      var io = new IntersectionObserver(function (entries) {
        var now = Date.now();
        var vh = window.innerHeight || document.documentElement.clientHeight || 0;
        entries.forEach(function (entry) {
          var s = stateFor(entry.target);
          if (!s) return;
          var need = Math.min(entry.boundingClientRect.height * 0.5, vh * 0.5);
          var inView = entry.isIntersecting && need > 0 && entry.intersectionRect.height >= need;
          if (inView) {
            if (!s.enteredAt) s.enteredAt = now;
            if (!s.viewed) { s.viewed = true; publish('section_view', base(s)); }
          } else if (s.enteredAt) {
            exitSection(s, now);
          }
        });
      }, { threshold: steps });
      state.forEach(function (s) { io.observe(s.el); });
    }

    function flush() {
      var now = Date.now();
      state.forEach(function (s) { if (s.viewed && !s.exited) exitSection(s, now); });
    }
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') flush();
    });

    document.addEventListener('click', function (ev) {
      var target = ev.target;
      if (!target || typeof target.closest !== 'function') return;
      var ctl = target.closest('a, button');
      if (!ctl) return;
      var sec = ctl.closest('[data-hpr-section]');
      if (!sec) return;
      var s = stateFor(sec);
      if (!s) return;
      var d = base(s);
      d.link_text = (ctl.getAttribute('aria-label') || ctl.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 80);
      d.link_url = (ctl.getAttribute('href') || '').slice(0, 200);
      publish('section_click', d);
    }, true);
  }

  function init() {
    initScrollReveal();
    initFindProductsDropdown();
    initTestimonialCarousel();
    initMotionLoops();
    initSectionAnalytics();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
